/**
 * src/components/notifications/NotificationDrawer.tsx
 * Ultra-Luxury Apple iOS 18 Notification Center
 * Features category squircles, dynamic filters, relative timestamps, rich actions, and glassmorphism.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { 
  Bell, 
  X, 
  Check, 
  Trash2, 
  ShieldAlert,
  ArrowRight,
  Filter,
  CheckCheck,
  Sparkles
} from 'lucide-react';
import { useNotifications, type InAppNotification } from '../../context/NotificationContext';
import { GlassButton } from '../ui/GlassButton';
import { registerOverlayDismiss } from '../../utils/modalNavigation';
import { haptic, playTapSound } from '../../utils/audioHaptics';

interface NotificationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateTab?: (tab: string) => void;
}

interface CategoryBadgeConfig {
  emoji: string;
  iconBg: string;
  borderColor: string;
  textColor: string;
  categoryLabel: string;
  actionText: string;
  targetTab: string;
}

const getCategoryConfig = (title: string, body: string, type?: string, url?: string): CategoryBadgeConfig => {
  const combined = (title + ' ' + body).toLowerCase();

  // Target tab determination
  let targetTab = 'approvals';
  if (url && url.includes('tab=')) {
    const match = url.match(/tab=([a-z0-9_-]+)/i);
    if (match && match[1]) targetTab = match[1];
  } else if (type === 'ledger_entry') {
    targetTab = 'ledger';
  } else if (type === 'payment_reminder') {
    targetTab = 'dashboard';
  }

  if (combined.includes('tea') || combined.includes('chai') || combined.includes('coffee') || combined.includes('soda') || combined.includes('snack')) {
    return {
      emoji: '☕',
      iconBg: 'rgba(245, 158, 11, 0.16)',
      borderColor: 'rgba(245, 158, 11, 0.35)',
      textColor: '#f59e0b',
      categoryLabel: 'Tea & Snacks',
      actionText: type === 'new_submission' ? 'Review Request' : 'View Entry',
      targetTab
    };
  }
  if (combined.includes('food') || combined.includes('lunch') || combined.includes('dinner') || combined.includes('meal')) {
    return {
      emoji: '🍽️',
      iconBg: 'rgba(249, 115, 22, 0.16)',
      borderColor: 'rgba(249, 115, 22, 0.35)',
      textColor: '#f97316',
      categoryLabel: 'Food & Meals',
      actionText: type === 'new_submission' ? 'Review Request' : 'View Entry',
      targetTab
    };
  }
  if (combined.includes('settle') || combined.includes('gpay') || combined.includes('upi') || combined.includes('payment received')) {
    return {
      emoji: '⚡',
      iconBg: 'rgba(139, 92, 246, 0.16)',
      borderColor: 'rgba(139, 92, 246, 0.35)',
      textColor: '#a855f7',
      categoryLabel: 'Settlement',
      actionText: 'View Details',
      targetTab
    };
  }
  if (type === 'payment_reminder' || combined.includes('reminder') || combined.includes('friendly reminder')) {
    return {
      emoji: '👋',
      iconBg: 'rgba(168, 85, 247, 0.16)',
      borderColor: 'rgba(168, 85, 247, 0.35)',
      textColor: '#c084fc',
      categoryLabel: 'Reminder',
      actionText: 'Settle via GPay',
      targetTab: 'dashboard'
    };
  }
  if (type === 'request_approved' || combined.includes('approved')) {
    return {
      emoji: '✅',
      iconBg: 'rgba(16, 185, 129, 0.16)',
      borderColor: 'rgba(16, 185, 129, 0.35)',
      textColor: '#10b981',
      categoryLabel: 'Approved',
      actionText: 'View Balance',
      targetTab: 'dashboard'
    };
  }
  if (type === 'request_rejected' || combined.includes('declined') || combined.includes('rejected')) {
    return {
      emoji: '❌',
      iconBg: 'rgba(239, 68, 68, 0.16)',
      borderColor: 'rgba(239, 68, 68, 0.35)',
      textColor: '#ef4444',
      categoryLabel: 'Declined',
      actionText: 'Check Reason',
      targetTab: 'approvals'
    };
  }
  if (combined.includes('travel') || combined.includes('fuel') || combined.includes('auto') || combined.includes('petrol')) {
    return {
      emoji: '🚕',
      iconBg: 'rgba(14, 165, 233, 0.16)',
      borderColor: 'rgba(14, 165, 233, 0.35)',
      textColor: '#0ea5e9',
      categoryLabel: 'Travel / Fuel',
      actionText: 'View Entry',
      targetTab
    };
  }
  if (combined.includes('shop') || combined.includes('grocer') || combined.includes('mart')) {
    return {
      emoji: '🛒',
      iconBg: 'rgba(6, 182, 212, 0.16)',
      borderColor: 'rgba(6, 182, 212, 0.35)',
      textColor: '#06b6d4',
      categoryLabel: 'Shopping',
      actionText: 'View Entry',
      targetTab
    };
  }

  // Default fallback
  return {
    emoji: '💵',
    iconBg: 'rgba(56, 189, 248, 0.16)',
    borderColor: 'rgba(56, 189, 248, 0.35)',
    textColor: '#38bdf8',
    categoryLabel: 'Ledger Alert',
    actionText: 'View Ledger',
    targetTab
  };
};

const formatRelativeTime = (isoString: string): string => {
  try {
    const d = new Date(isoString);
    const now = Date.now();
    const diffSec = Math.floor((now - d.getTime()) / 1000);

    if (diffSec < 45) return 'Just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 172800) return 'Yesterday';
    return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
  } catch {
    return isoString;
  }
};

export const NotificationDrawer: React.FC<NotificationDrawerProps> = ({
  isOpen,
  onClose,
  onNavigateTab
}) => {
  const {
    permission,
    notifications,
    unreadCount,
    requestPermission,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    removeNotification,
    triggerAllDemoNotifications
  } = useNotifications();

  const [activeFilter, setActiveFilter] = useState<'all' | 'unread' | 'requests' | 'reminders'>('all');

  useEffect(() => {
    if (!isOpen) return;
    const unregister = registerOverlayDismiss(onClose);
    return () => unregister();
  }, [isOpen, onClose]);

  const filteredNotifications = useMemo(() => {
    return notifications.filter(n => {
      if (activeFilter === 'unread') return !n.read;
      if (activeFilter === 'requests') {
        const text = (n.title + ' ' + n.body).toLowerCase();
        return n.type === 'new_submission' || text.includes('request') || text.includes('tea') || text.includes('lunch') || text.includes('snack');
      }
      if (activeFilter === 'reminders') {
        return n.type === 'payment_reminder' || (n.title + ' ' + n.body).toLowerCase().includes('reminder');
      }
      return true;
    });
  }, [notifications, activeFilter]);

  if (!isOpen) return null;

  const handleNotificationClick = (notif: InAppNotification, config: CategoryBadgeConfig) => {
    haptic.tap();
    playTapSound();
    markAsRead(notif.id);
    onClose();
    if (onNavigateTab) {
      onNavigateTab(config.targetTab);
    }
  };

  const drawerElement = (
    <div
      className="glass-modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100000,
        pointerEvents: 'auto',
        justifyContent: 'flex-end',
        alignItems: 'stretch',
        padding: 0,
        backdropFilter: 'blur(10px)',
        backgroundColor: 'rgba(0, 0, 0, 0.45)'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <aside
        style={{
          width: '100%',
          maxWidth: '430px',
          background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.94) 0%, rgba(10, 15, 29, 0.98) 100%)',
          backdropFilter: 'blur(35px) saturate(190%)',
          borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          boxShadow: '-16px 0 50px rgba(0, 0, 0, 0.65)',
          animation: 'slideInRight 0.28s cubic-bezier(0.16, 1, 0.3, 1)',
          pointerEvents: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Apple iOS 18 Glassmorphic Header */}
        <div style={{
          padding: '1.25rem 1.25rem 0.85rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'rgba(255, 255, 255, 0.02)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '38px',
              height: '38px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.2) 0%, rgba(99, 102, 241, 0.2) 100%)',
              border: '1px solid rgba(56, 189, 248, 0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#38bdf8',
              boxShadow: '0 4px 12px rgba(56, 189, 248, 0.15)'
            }}>
              <Bell size={20} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2 style={{ fontSize: '1.15rem', fontWeight: 800, margin: 0, color: '#ffffff', letterSpacing: '-0.02em' }}>
                  Notifications
                </h2>
                {unreadCount > 0 && (
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #0284c7 0%, #2563eb 100%)',
                    color: '#ffffff',
                    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.4)'
                  }}>
                    {unreadCount} New
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '2px' }}>
                Instant real-time ledger & request alerts
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            className="ios-tap-target"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: 'rgba(255, 255, 255, 0.7)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s ease'
            }}
            title="Close"
          >
            <X size={17} />
          </button>
        </div>

        {/* Filter Segmented Control Pills */}
        <div style={{
          padding: '0.75rem 1.25rem',
          display: 'flex',
          gap: '6px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          background: 'rgba(0, 0, 0, 0.2)',
          overflowX: 'auto',
          scrollbarWidth: 'none'
        }}>
          {[
            { id: 'all', label: 'All', count: notifications.length },
            { id: 'unread', label: 'Unread', count: unreadCount },
            { id: 'requests', label: '☕ Requests' },
            { id: 'reminders', label: '👋 Reminders' }
          ].map(tab => {
            const isActive = activeFilter === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  haptic.tap();
                  setActiveFilter(tab.id as any);
                }}
                style={{
                  padding: '5px 12px',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: isActive ? 700 : 500,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  border: isActive ? '1px solid rgba(56, 189, 248, 0.4)' : '1px solid rgba(255, 255, 255, 0.08)',
                  background: isActive ? 'linear-gradient(135deg, rgba(56, 189, 248, 0.2), rgba(37, 99, 235, 0.25))' : 'rgba(255, 255, 255, 0.03)',
                  color: isActive ? '#38bdf8' : 'rgba(255, 255, 255, 0.65)',
                  transition: 'all 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <span>{tab.label}</span>
                {tab.count !== undefined && tab.count > 0 && (
                  <span style={{
                    fontSize: '0.68rem',
                    opacity: isActive ? 1 : 0.6,
                    padding: '0 4px',
                    borderRadius: '6px',
                    background: isActive ? 'rgba(56, 189, 248, 0.25)' : 'rgba(255, 255, 255, 0.08)'
                  }}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Permission Request Prompt Banner (If not yet allowed) */}
        {permission !== 'granted' && (
          <div style={{
            margin: '0.85rem 1.25rem 0',
            padding: '0.85rem 1rem',
            borderRadius: '14px',
            background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.12), rgba(99, 102, 241, 0.12))',
            border: '1px solid rgba(56, 189, 248, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.6rem'
          }}>
            <div style={{ display: 'flex', gap: '0.625rem', alignItems: 'center' }}>
              <ShieldAlert size={17} style={{ color: '#38bdf8', flexShrink: 0 }} />
              <div style={{ fontSize: '0.78rem', color: 'rgba(255, 255, 255, 0.85)', lineHeight: 1.35 }}>
                <strong style={{ color: '#ffffff' }}>Enable Push Alerts on this Device:</strong>
                <div style={{ fontSize: '0.72rem', color: 'rgba(255, 255, 255, 0.6)' }}>Get instant vibration & sound when requests are submitted.</div>
              </div>
            </div>
            <GlassButton
              variant="primary"
              onClick={requestPermission}
              style={{ width: '100%', justifyContent: 'center', padding: '0.45rem', fontSize: '0.75rem', borderRadius: '10px' }}
            >
              Turn On Push Notifications
            </GlassButton>
          </div>
        )}

        {/* Quick Toolbar (Mark all as read / Test Alerts / Clear) */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.6rem 1.25rem',
          fontSize: '0.72rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
          background: 'rgba(255, 255, 255, 0.015)'
        }}>
          <button
            type="button"
            onClick={() => {
              haptic.tap();
              markAllAsRead();
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(56, 189, 248, 0.9)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
              fontWeight: 600,
              padding: '4px 6px',
              borderRadius: '6px'
            }}
          >
            <CheckCheck size={14} />
            Mark all read
          </button>

          {/* 1-Tap Trigger Test Alerts Button */}
          <button
            type="button"
            onClick={() => {
              haptic.tap();
              triggerAllDemoNotifications();
            }}
            className="ios-tap-target"
            style={{
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(234, 88, 12, 0.15) 100%)',
              border: '1px solid rgba(245, 158, 11, 0.35)',
              color: '#f59e0b',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
              fontWeight: 700,
              padding: '4px 10px',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.15)',
              transition: 'all 0.15s ease'
            }}
            title="Trigger all notification types locally"
          >
            <Sparkles size={13} />
            Test 1x Alerts
          </button>

          <button
            type="button"
            onClick={() => {
              haptic.tap();
              clearNotifications();
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255, 255, 255, 0.4)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
              fontWeight: 500,
              padding: '4px 6px',
              borderRadius: '6px'
            }}
          >
            <Trash2 size={13} />
            Clear
          </button>
        </div>

        {/* Notifications Scroll Deck */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '0.85rem 1.25rem 2rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          scrollbarWidth: 'thin'
        }}>
          {filteredNotifications.length === 0 ? (
            <div style={{
              flex: 1,
              minHeight: '280px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              color: 'rgba(255, 255, 255, 0.4)',
              gap: '0.85rem'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(56, 189, 248, 0.5)'
              }}>
                <Bell size={28} strokeWidth={1.5} />
              </div>
              <div>
                <div style={{ fontWeight: 700, color: '#ffffff', fontSize: '0.95rem' }}>
                  {activeFilter === 'unread' ? 'No Unread Notifications' : 'All Caught Up!'}
                </div>
                <div style={{ fontSize: '0.78rem', marginTop: '0.25rem', color: 'rgba(255, 255, 255, 0.5)', maxWidth: '240px' }}>
                  {activeFilter === 'unread' 
                    ? 'All alerts have been reviewed.'
                    : 'New transaction requests, approvals and reminders will appear here.'}
                </div>
              </div>
            </div>
          ) : (
            filteredNotifications.map(n => {
              const config = getCategoryConfig(n.title, n.body, n.type, n.url);
              const isUnread = !n.read;

              return (
                <div
                  key={n.id}
                  onClick={() => handleNotificationClick(n, config)}
                  className="ios-tap-target"
                  style={{
                    padding: '0.95rem 1rem',
                    borderRadius: '16px',
                    background: isUnread 
                      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.75) 0%, rgba(15, 23, 42, 0.85) 100%)'
                      : 'rgba(255, 255, 255, 0.025)',
                    border: isUnread 
                      ? '1px solid rgba(56, 189, 248, 0.35)' 
                      : '1px solid rgba(255, 255, 255, 0.06)',
                    cursor: 'pointer',
                    transition: 'all 0.18s cubic-bezier(0.16, 1, 0.3, 1)',
                    position: 'relative',
                    boxShadow: isUnread ? '0 4px 18px rgba(0, 0, 0, 0.25), 0 0 12px rgba(56, 189, 248, 0.08)' : 'none'
                  }}
                >
                  {/* Unread Glow Dot & Dismiss Button */}
                  <div style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {isUnread && (
                      <span 
                        title="Unread"
                        style={{
                          width: '7px',
                          height: '7px',
                          borderRadius: '50%',
                          background: '#38bdf8',
                          boxShadow: '0 0 8px #38bdf8'
                        }} 
                      />
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        haptic.tap();
                        removeNotification(n.id);
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'rgba(255, 255, 255, 0.35)',
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'color 0.15s ease'
                      }}
                      title="Dismiss alert"
                    >
                      <X size={13} />
                    </button>
                  </div>

                  <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'flex-start' }}>
                    {/* Rich Category Emoji Squircle Avatar */}
                    <div style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '13px',
                      background: config.iconBg,
                      border: `1px solid ${config.borderColor}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                      flexShrink: 0,
                      boxShadow: `0 4px 12px ${config.iconBg}`
                    }}>
                      {config.emoji}
                    </div>

                    {/* Content Block */}
                    <div style={{ flex: 1, minWidth: 0, paddingRight: '1.25rem' }}>
                      {/* Category Tag & Relative Time */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                        <span style={{
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          color: config.textColor,
                          background: config.iconBg,
                          padding: '1px 6px',
                          borderRadius: '5px'
                        }}>
                          {config.categoryLabel}
                        </span>
                        <span style={{ fontSize: '0.68rem', color: 'rgba(255, 255, 255, 0.4)' }}>•</span>
                        <span style={{ fontSize: '0.68rem', color: 'rgba(255, 255, 255, 0.45)' }}>
                          {formatRelativeTime(n.timestamp)}
                        </span>
                      </div>

                      {/* Notification Title */}
                      <div style={{
                        fontWeight: isUnread ? 800 : 600,
                        fontSize: '0.875rem',
                        color: isUnread ? '#ffffff' : 'rgba(255, 255, 255, 0.85)',
                        lineHeight: 1.3,
                        marginBottom: '4px'
                      }}>
                        {n.title}
                      </div>

                      {/* Notification Body */}
                      <div style={{
                        fontSize: '0.78rem',
                        color: isUnread ? 'rgba(255, 255, 255, 0.75)' : 'rgba(255, 255, 255, 0.55)',
                        lineHeight: 1.4,
                        wordBreak: 'break-word',
                        marginBottom: '8px'
                      }}>
                        {n.body}
                      </div>

                      {/* Direct Action Link Pill */}
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        color: config.textColor,
                        opacity: isUnread ? 1 : 0.75
                      }}>
                        <span>{config.actionText}</span>
                        <ArrowRight size={12} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </aside>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(drawerElement, document.body)
    : drawerElement;
};
